import { Box, Tooltip } from '@material-ui/core';
import { MerchantStructure, User, UserType } from '@nemuru-eng/js-api-client';

import { useStructureUser } from '@/components/shared/components/UserList/components/UserTable/components/UserTableBody/components/UserActionsRowDialog/components/ChangeUser/hooks/useStructureUser';
import { Font } from '@/components/shared/components/Font';
import { Icon } from '@/components/shared/components/Icon';
import { Text } from '@/components/shared/components/Text';
import { useCustomTheme } from '@/hooks/useCustomTheme';
import { useScreen } from '@/hooks/useScreen';
import { UserInStructure } from '@/types/UserInStructure';

interface SelectedUserStructureProps {
  selectedUser: User | UserInStructure;
  merchantStructure: MerchantStructure;
  hideSelectedUser?: boolean;
  mainBoxProps?: React.ComponentProps<typeof Box>;
}
export const SelectedUserStructure = ({
  selectedUser,
  merchantStructure,
  hideSelectedUser = false,
  mainBoxProps,
}: SelectedUserStructureProps) => {
  const { palette, customBorderRadius } = useCustomTheme();
  const { isMobile } = useScreen();

  const { selectedUserType, selectedUserMerchant, selectedUserChannel, selectedUserAgent } = useStructureUser({
    selectedUser,
    merchantStructure,
  });

  const UserWrapper = ({ children }) => (
    <Box
      display={'flex'}
      flexDirection={'column'}
      padding={1}
      minWidth={'60px'}
      alignItems={isMobile ? 'center' : 'flex-start'}
    >
      {children}
    </Box>
  );

  const ChevronRightIcon = () => (
    <Icon color={palette.grey[400]} style={{ marginInline: '2px' }}>
      {isMobile ? 'expand_more' : 'chevron_right'}
    </Icon>
  );

  const userDescriptionFontStyle = {
    maxWidth: isMobile ? '100%' : '145px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  };

  return (
    <>
      {!hideSelectedUser && (
        <Box textAlign={'center'} p={1}>
          <Font variant="body1" color={'textPrimary'}>
            <strong>
              {selectedUserType === UserType.MERCHANT && 'businessName' in selectedUser
                ? selectedUser.businessName
                : 'username' in selectedUser && selectedUser.username}
            </strong>
          </Font>
        </Box>
      )}
      <Box
        my={2}
        py={isMobile ? 2 : 0}
        borderRadius={customBorderRadius.paper}
        minHeight={'60px'}
        display={'flex'}
        width={'100%'}
        justifyContent={'center'}
        flexDirection={isMobile ? 'column' : 'row'}
        alignItems={'center'}
        bgcolor={palette.grey[50]}
        {...mainBoxProps}
      >
        {selectedUserMerchant && (
          <UserWrapper>
            <Font variant="overline">
              <Text tId="MERCHANT" />
            </Font>
            <Font variant="subtitle2" color="textPrimary" style={userDescriptionFontStyle}>
              <Tooltip placement="bottom" title={selectedUserMerchant}>
                <strong>{selectedUserMerchant}</strong>
              </Tooltip>
            </Font>
          </UserWrapper>
        )}
        {selectedUserChannel && (
          <>
            <ChevronRightIcon />
            <UserWrapper>
              <Font variant="overline">
                <Text tId="CHANNEL" />
              </Font>
              <Font variant="subtitle2" color="textPrimary" style={userDescriptionFontStyle}>
                <Tooltip placement="bottom" title={selectedUserChannel.description}>
                  <strong>{selectedUserChannel.description}</strong>
                </Tooltip>
              </Font>
            </UserWrapper>
          </>
        )}
        {selectedUserAgent && (
          <>
            <ChevronRightIcon />
            <UserWrapper>
              <Font variant="overline">
                <Text tId="AGENT" />
              </Font>
              <Font variant="subtitle2" color="textPrimary" style={userDescriptionFontStyle}>
                <Tooltip placement="bottom" title={selectedUserAgent.name}>
                  <strong>{selectedUserAgent.name}</strong>
                </Tooltip>
              </Font>
            </UserWrapper>
          </>
        )}
      </Box>
    </>
  );
};
